import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { DialogChildProps } from "../config";
import ComboBoxOption from "../coreui/ComboBoxOption";
import Select from "../coreui/Select";
import { TableVerticalLayoutProps } from "../coreui/Table";
import TextField from "../coreui/TextField";
import PaneRow, { RuntimeWidget } from "../models/PaneRow";
import RoundTripService from "../services/RoundTripService";
import ErrorsStore from "../stores/ErrorsStore";
import { AccessLevel } from "./AccessLevel";

interface ConfigProperties {
  dataId: string;
  disabledHelpText: string;
  helperText: string;
  label: string;
  name: string;
  propagated?: DialogChildProps & TableVerticalLayoutProps;
  roundTripOnChange: boolean;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
  options: ComboBoxOption[];
  selectedDisplayValue: string;
  selectedValue: string | null;
  showAsMandatory: boolean;
  showDisabledHelp: boolean;
}

export class RelationshipComboBox extends React.Component<ConfigProperties> {
  private dataId: string = "";
  private dialogRowKey?: string;
  private name: string = "";
  private rowKey?: string;

  public constructor(props: ConfigProperties) {
    super(props);

    makeObservable<
      RelationshipComboBox,
      "dataId" | "name" | "onValueChange" | "syncDerivedWithProps"
    >(this, {
      dataId: observable,
      name: observable,
      onValueChange: action,
      syncDerivedWithProps: action,
    });

    this.syncDerivedWithProps();
  }
  private getOptions(runtimeProps: RuntimeProperties) {
    // Check if Widget is rendered in the vertical layout of a grid
    if (this.props.propagated?.parentTable) {
      const selectedOption: ComboBoxOption = {
        display: runtimeProps.selectedDisplayValue,
        value: runtimeProps.selectedValue,
      };

      const table = this.props.propagated.parentTable;
      return table.getRelationshipComboBoxOptions(
        this.props.name,
        selectedOption
      );
    }

    const options = [...runtimeProps.options];

    if (runtimeProps.selectedValue) {
      // Add the initially selected value as a historic option if is not
      // among the current candidates
      const optionInList = options.find(
        (o) => o.value === runtimeProps.selectedValue
      );

      if (optionInList === undefined) {
        const historicOption: ComboBoxOption = {
          display: runtimeProps.selectedDisplayValue,
          historic: true,
          value: runtimeProps.selectedValue,
        };

        options.push(historicOption);
      }
    }

    return options;
  }

  private onValueChange = (value: ComboBoxOption | null) => {
    const row = PaneRow.get(this.dataId, this.rowKey);
    const widget = row!.getWidgetT<string | null, RuntimeProperties>(this.name);

    const oldValue = widget.value;

    ErrorsStore.clearBusinessErrorsForWidget(this.dataId, this.name);
    widget.setValue(value?.value || null);

    if (!this.props.roundTripOnChange) {
      return;
    }

    RoundTripService.standardRoundTrip(
      "RelationshipComboBox/OnChange",
      { dataId: this.dataId, name: this.name },
      {
        dialogRowKey: this.dialogRowKey,
      }
    ).catch((reason) => {
      if (reason) {
        throw reason;
      } else {
        // If the round trip fails, undo the value change.
        widget.setValue(oldValue);
      }
    });
  };

  private syncDerivedWithProps(): void {
    this.dataId = this.props.dataId;
    this.name = this.props.name;
    this.dialogRowKey =
      this.props.propagated?.parentDialog?.rowKey || undefined;

    this.rowKey = undefined;
    if (this.props.propagated?.parentTable) {
      // Widget is rendered in the vertical layout of a grid.
      this.rowKey = this.props.propagated.rowKey;
    }
  }

  public componentDidUpdate(): void {
    this.syncDerivedWithProps();
  }

  public render(): React.ReactNode {
    const row = PaneRow.get(this.dataId, this.rowKey);
    if (!row) {
      return null;
    }

    const widget = row.getWidgetT<string | null, RuntimeProperties>(this.name);

    if (widget.properties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    if (widget.properties.accessLevel === AccessLevel.disabled) {
      return (
        <TextField
          disabled={true}
          disabledHelpText={
            widget.properties.showDisabledHelp
              ? this.props.disabledHelpText
              : undefined
          }
          label={this.props.label}
          name={this.props.name}
          variant="filled"
        />
      );
    }

    const options: ComboBoxOption[] = this.getOptions(widget.properties);

    return (
      <Select
        getErrors={() => widget.properties.businessErrors}
        helperText={this.props.helperText}
        label={this.props.label}
        onValueChange={this.onValueChange}
        options={options}
        readOnly={widget.properties.accessLevel === AccessLevel.readOnly}
        showAsMandatory={widget.properties.showAsMandatory}
        value={widget.value}
      />
    );
  }
}

export default observer(RelationshipComboBox);
